import { useState, useEffect, useMemo, useRef } from 'react';

import { Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';

import { CustomNotification } from 'common/components';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import { useQaSpecGrid } from './useQaSpecGrid';

import { getAllRows } from 'pages/qa-spec/utils';
import { apiHandler } from 'utils/api';
import {
  transformBooleanToStringQaSpecValue,
  transformStringToBooleanQaSpecValue,
} from 'pages/qa-spec/utils/transformQaBooleanValue';

import * as qaSpecServices from 'services/qaSpec';

import { useGetOrganolepticQuery } from 'pages/qa-spec/query';

import useCheckSnapshotForRetailer from './useCheckSnapshotForRetailer';

import { validateOrganolepticCharacteristicGrid } from 'pages/qa-spec/components/qa-prod-spec/utils';
import { sleep } from 'utils/delay';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/qa-spec';

const textEditorRenderer =
  (maxLength = 150) =>
  () => {
    return {
      component: 'TextEditor',
      popup: true,
      params: {
        maxLength: maxLength,
      },
    };
  };

export const useOrganoleptic = ({
  productId,
  isGridEditing,
  requiredFields,
  isSectionRequired,
}) => {
  const intl = useIntl();
  const gridInst = useRef();

  const [saveLoading, setSaveLoading] = useState(false);

  const isToggleRequiredFields = !isEmpty(requiredFields);

  const initRowData = (gridData) => {
      const defaultData = [
          {
              parameter: 'Appearance',
          },
          {
              parameter: 'Color',
          },
          {
              parameter: 'Aroma/Odor',
          },
          {
              parameter: 'Flavor/Taste',
          },
          {
              parameter: 'Texture',
          },
          {
              parameter: 'Workmanship'
          },
          {
              parameter: 'Function'
          }
      ];

    let procData = [...defaultData];

    if (gridData?.length) {
      // first approach: take parameter from grid data and compare with defaultItem
      /* procData = gridData.map((dataItem) => {
        const defaultItem = defaultData.find(
          (d) => d.parameter === dataItem.parameter,
        );

        return {
          ...defaultItem,
          ...dataItem,
        };
      }); */

      // second approach: take defaultItem as source of truth
      const gridMap = Object.fromEntries(
        gridData.map((item) => [item.parameter, item]),
      );

      procData = defaultData.map((defaultItem) => ({
        ...defaultItem,
        ...gridMap[defaultItem.parameter],
      }));
    }

    return procData;
  };

  const resetGridCallback = () => {
    if (isToggleRequiredFields) {
      validateOrganolepticCharacteristicGrid({
        grid: gridInst,
        requiredFields,
      }).validateAllRows();
    }
  };

  const { organolepticData, handleRefetchOrganoleptic } =
    useGetOrganolepticQuery({
      productId,
      enabled: !!productId,
    });

  const { fetchDataLoading, rowData, resetGridData } = useQaSpecGrid({
    gridInst,
    gridData: organolepticData?.organolepticCharacteristics,
    isGridEditing,
    customInitRowData: initRowData,
    resetGridCallback,
  });

  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const columnDefs = useMemo(() => {
    const statusField = {
      minWidth: 50,
      maxWidth: 50,
      field: 'error',
      displayName: ' ',
      cellRenderer: 'StatusRender',
      type: 'nonEditableColumn',
    };

    let cols = [
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Parameter',
        field: 'parameter',
        editable: false,
        width: 200,
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.parameter',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Preparation Method',
        field: 'preparationMethod',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.preparationMethod',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Specification/Description/Target',
        field: 'specification',
        cellEditorSelector: textEditorRenderer(5000),
        minWidth: 300,
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.specification',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Tolerance',
        field: 'tolerance',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.tolerance',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Reject/Fail Criteria',
        field: 'rejectOrFailCriteria',
        width: 200,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.rejectOrFailCriteria',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Frequency',
        field: 'frequency',
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.frequency',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Release Criteria',
        field: 'releaseCriteria',
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Yes', displayName: 'Yes' },
                { value: 'No', displayName: 'No' },
                { value: 'Not Applicable', displayName: 'Not Applicable' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.organolepticCharacteristics.releaseCriteria',
        },
      },
    ];

    cols = isToggleRequiredFields ? [statusField, ...cols] : cols;

    return cols.map((fieldItem) => {
      const DEFAULT_WIDTH = 150;

      let initColSetting = {
        type: 'editableColumn',
      };

      return {
        minWidth: DEFAULT_WIDTH,
        ...initColSetting,
        ...fieldItem,
      };
    });
  }, [isToggleRequiredFields, isToggleSnapshot]);

  const saveData = async (toggleEditMode, analysisNotExecuted) => {
    gridInst.current.api.stopEditing();

    const GRID_VALIDATION_DELAY = 700;
    await sleep(GRID_VALIDATION_DELAY);

    // const isGridHaveError = validateOrganolepticCharacteristicGrid({
    //   grid: gridInst,
    //   requiredFields,
    // }).checkIfGridHaveError();

    // if (isGridHaveError) {
    //   CustomNotification.error(
    //     `Please resolve the Organoleptic Characteristics grid's errors.`,
    //   );
    //   return;
    // }

    setSaveLoading(true);

    const allRowData = getAllRows(gridInst);

    const params = {
      productId,
      organolepticCharacteristics: allRowData,
      analysisNotExecuted:
        transformStringToBooleanQaSpecValue(analysisNotExecuted),
    };

    const successMessage = intl.formatMessage(
      Messages.productSpecSaveOrganolepticSuccess,
    );
    const errorMessage = intl.formatMessage(
      Messages.productSpecSaveOrganolepticError,
    );

    apiHandler({
      service: qaSpecServices.saveQaSpecOrganolepticCharacteristic,
      params,
      successMessage,
      errorMessage,
      successCallback: saveSuccessCallback(toggleEditMode),
      onFinally: saveFinally,
    });
  };

  const saveSuccessCallback = (toggleEditMode) => () => {
    toggleEditMode();

    handleRefetchOrganoleptic();
  };

  const saveFinally = () => {
    setSaveLoading(false);
  };

  const cancelEdit = () => {
    resetGridData();
  };

  const onCellEditingStopped = async (event) => {
    if (isToggleRequiredFields) {
      await validateOrganolepticCharacteristicGrid({
        grid: gridInst,
        requiredFields,
      }).checkAllRequiredFieldsByParameterType({ event });
    }
  };

  useEffect(() => {
    const handleValidateAllRowDataForSupplier = async () => {
      if (rowData && rowData?.length > 0 && isToggleRequiredFields) {
        await validateOrganolepticCharacteristicGrid({
          grid: gridInst,
          requiredFields,
        }).validateAllRows();
      }
    };

    handleValidateAllRowDataForSupplier();
  }, [
    isToggleRequiredFields,
    JSON.stringify(rowData),
    JSON.stringify(requiredFields),
  ]);

  const gridTitle = (
    <>
      {isToggleRequiredFields && isSectionRequired && (
        <>
          <Typography.Text type='danger'>*</Typography.Text>{' '}
        </>
      )}
      Organoleptic Characteristics
    </>
  );

  return {
    gridInst,
    analysisNotExecuted: transformBooleanToStringQaSpecValue(
      organolepticData?.analysisNotExecuted,
    ),
    rowData,
    columnDefs,
    saveData,
    cancelEdit,
    loading: fetchDataLoading || saveLoading,
    onCellEditingStopped,
    isToggleRequiredFields,
    gridTitle,
  };
};
